import { CASES } from "./constants";
import { safeGetJSON } from "tap/utils/safeLocalStorage";
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  getInitialPaginationRange,
  getStoredItemsPerPage,
  PAGINATION_STORAGE_KEYS,
  persistItemsPerPageFromPayload,
  withPreservedPagination,
} from "tap/utils/paginationPreferences.js";

const paginationKeys = {
  itemsPerPage: PAGINATION_STORAGE_KEYS.updateEngine,
  jobViewItemsPerPage: PAGINATION_STORAGE_KEYS.updateEngineJobView,
};
const initialItemsPerPage = getStoredItemsPerPage(paginationKeys.itemsPerPage);
const initialJobViewItemsPerPage = getStoredItemsPerPage(
  paginationKeys.jobViewItemsPerPage,
);
const initialPaginationRange = getInitialPaginationRange(initialItemsPerPage);

/**
 * Retrieves persisted sort preference from localStorage.
 * @returns {Array} Sort tuple [field, direction], defaults to ['id', 'DESC']
 */
const getInitialJobsSort = () => {
  return safeGetJSON("updateEngineSort", ["id", "DESC"]);
};

/**
 * Initial state for the job view modal (task list within a job).
 * Separated for use with CLEAR_JOB_VIEW_MODAL action.
 */
const initialJobViewState = {
  jobTasks: [],
  jobViewFilterForm: {
    jobId: "",
    jobStatus: "",
    dealsProcessed: "",
    spaJobType: "TAP_UPDATE_ENGINE",
    taskStatus: {
      value: "",
      label: "All",
    },
  },
  isJobViewModalOpen: false,
  taskStatusOptions: [
    { value: "", label: "All" },
    { value: "COMPLETE", label: "Successes" },
    { value: "INVALID", label: "Failures" },
  ],
  selectedJob: "",
  /** Job statuses that indicate the job is still processing and may need polling */
  pendingStatuses: [
    "RUNNING",
    "CREATED",
    "STARTING",
    "RETRYING",
    "QUEUED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED",
    "QUEUED_UPDATED_USER_INITIATED",
    "QUEUED_ADMINISTRATOR",
    "QUEUED_UPDATED_ADMINISTRATOR",
  ],
  jobViewTotalPages: 0,
  jobViewCurrentPage: 0,
  jobViewTotalItems: 0,
  jobViewItemsPerPage: initialJobViewItemsPerPage,
};

/**
 * UpdateEngine Redux State Shape
 * @typedef {Object} UpdateEngineState
 * @property {Array} jobs - List of update engine job records from API
 * @property {boolean} refreshJobs - Flag to trigger grid data refetch
 * @property {Object} filterForm - Search/filter form values (jobId, jobType fixed to TAP_UPDATE_ENGINE, jobStatus)
 * @property {Array} jobStatusOptions - Job status dropdown options (from API)
 * @property {Array} jobTypeOptions - Job type dropdown options (from API)
 * @property {number} totalPages - Pagination total pages
 * @property {number} totalItems - Total record count
 * @property {Array} updateEngineRange - Pagination range [start, end]
 * @property {Array} updateEngineSort - Sort tuple [field, direction]
 */
const initialState = {
  jobs: [],
  refreshJobs: false,
  filterForm: {
    jobId: "",
    jobType: "TAP_UPDATE_ENGINE",
    jobStatus: "",
  },
  jobStatusOptions: [],
  jobTypeOptions: [],
  totalPages: 0,
  totalItems: 0,
  currentPage: 0,
  filterParam: "",
  updateEngineRange: initialPaginationRange,
  updateEngineSort: getInitialJobsSort(),
  defaultFilterRange: initialPaginationRange,
  itemsPerPage: initialItemsPerPage,
  pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
  ...initialJobViewState,
};

export function updateEngine(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case CASES.UPDATE_UPDATE_ENGINE: {
      // Persist sort preference to localStorage for session continuity
      if (payload.updateEngineSort) {
        localStorage.setItem(
          "updateEngineSort",
          JSON.stringify(payload.updateEngineSort),
        );
      }
      persistItemsPerPageFromPayload(payload, paginationKeys);
      return { ...state, ...payload };
    }
    case CASES.UPDATE_UPDATE_ENGINE_SEARCH_FORM: {
      const { data, key } = payload;
      return { ...state, [key]: { ...state[key], ...data } };
    }
    case CASES.CLEAR_UPDATE_ENGINE: {
      return withPreservedPagination(
        state,
        {
          ...initialState,
          jobStatusOptions: state.jobStatusOptions,
          jobTypeOptions: state.jobTypeOptions,
          updateEngineSort: state.updateEngineSort,
        },
        ["updateEngineRange"],
        ["itemsPerPage", "jobViewItemsPerPage"],
      );
    }
    case CASES.CLEAR_JOB_VIEW_MODAL: {
      return {
        ...state,
        ...initialJobViewState,
        jobViewItemsPerPage: state.jobViewItemsPerPage,
      };
    }
    default:
      return state;
  }
}
